import clsx from 'clsx';
import React from 'react';
import Button from '../../atoms/Button/Button';
import Icon from '../../atoms/Icon/Icon';
import AccordionPanel from './AccordionPanel';
import useAccordion from './useAccordion';

export type AccordionTab = {
  children?: React.ReactNode;
  className?: string;
  customRight?: React.ReactNode;
  icon?: React.ReactNode;
  title: React.ReactNode;
};

export type AccordionProps = {
  activeTabs?: number[];
  hasIconHide?: boolean;
  isMulti?: boolean;
  onTabToggle?: (id: number, currentTabs: number[]) => void;
  position?: 'left' | 'right';
  tabs?: AccordionTab[];
};

/**
 * Chevron indicador de estado. En modo por defecto es un único `chevron-down`
 * que rota 180º al abrir (vía CSS con el selector `[aria-expanded="true"]`). En
 * modo `hasIconHide` son dos iconos (`chevron-down`/`chevron-up`) que se
 * intercambian por opacidad. `aria-hidden` porque el estado lo comunica el
 * `aria-expanded` del propio botón.
 */
function AccordionChevron({ hasIconHide }: { hasIconHide: boolean }): JSX.Element {
  return (
    <span aria-hidden="true" className={clsx('ui-accordion__chevron', { 'ui-accordion__chevron--swap': hasIconHide })}>
      <span className="ui-accordion__chevron-icon ui-accordion__chevron-icon--down">
        <Icon color="inherit" name="chevron-down" size="lg" />
      </span>
      {hasIconHide && (
        <span className="ui-accordion__chevron-icon ui-accordion__chevron-icon--up">
          <Icon color="inherit" name="chevron-up" size="lg" />
        </span>
      )}
    </span>
  );
}

/**
 * Accordion — versión Design System v2 del acordeón propio (no MUI).
 *
 * La cabecera de cada panel es el átomo `Button` (variant `plain`), por lo que
 * es un `<button>` nativo: teclado (Enter/Espacio), foco y semántica de botón
 * vienen de fábrica. La relación de accesibilidad se declara con
 * `aria-expanded`/`aria-controls` en la cabecera y `role="region"` +
 * `aria-labelledby` en el panel.
 */
function Accordion({
  activeTabs,
  hasIconHide = false,
  isMulti = false,
  onTabToggle = () => {},
  position = 'left',
  tabs = [],
}: AccordionProps): JSX.Element {
  const { renderTabs } = useAccordion({ activeTabs, isMulti, onTabToggle, tabs });

  return (
    <div className={clsx('ui-accordion', `ui-accordion--${position}`)}>
      {renderTabs.map(({ headerId, isOpen, onToggle, panelId, tab }) => (
        <div key={headerId} className={clsx('ui-accordion__item', tab.className)}>
          {tab.icon && <span className="ui-accordion__item-icon">{tab.icon}</span>}

          <Button
            ariaControls={panelId}
            ariaExpanded={isOpen}
            className="ui-accordion__trigger"
            id={headerId}
            isFullWidth
            onClick={onToggle}
            variant="plain"
          >
            <span className="ui-accordion__title">
              {position === 'left' && <AccordionChevron hasIconHide={hasIconHide} />}
              <span className="ui-accordion__title-text">{tab.title}</span>
            </span>
            {position === 'left' && tab.customRight && <span className="ui-accordion__custom-right">{tab.customRight}</span>}
            {position === 'right' && <AccordionChevron hasIconHide={hasIconHide} />}
          </Button>

          {isOpen && (
            <AccordionPanel id={panelId} labelledBy={headerId}>
              {tab.children}
            </AccordionPanel>
          )}
        </div>
      ))}
    </div>
  );
}

export default Accordion;
